import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { DollarSign, TrendingUp, FileText, ChevronDown, ChevronUp, Download, AlertCircle, Receipt, Minus, Shield, Map, Route } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatFinalizedDate } from '@/lib/utils';
import { numOrDefault } from '@/lib/invoiceHelpers';
import { isNativePlatform } from '@/lib/backgroundLocation';
import ExpensesTab from '@/components/ExpensesTab';
import IFTATab from '@/components/IFTATab';
import { findMyDriverProfile } from '@/lib/myDriverProfile';
import PendingApprovalNotice from '@/components/PendingApprovalNotice';
import CompleteTaxFormModal from '@/components/CompleteTaxFormModal';
import { Button } from '@/components/ui/button';

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-primary', bg = 'bg-primary/10' }) => (
  <div className="glass-card flex-1 w-full max-w-full box-border p-4 h-full">
    <div className="flex items-start justify-between mb-3">
      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", bg)}>
        <Icon className={cn("w-5 h-5", color)} />
      </div>
    </div>
    <p className="text-2xl font-space font-bold text-foreground">{value}</p>
    <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const statusColors = {
  'Pending': 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  'Approved': 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
  'In Batch': 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  'Paid': 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
};

// Recompute net pay — insurance/ELD are weekly payroll deductions, NOT per-load
function computeNetPay(s, lineItemsMap = {}) {
  if (s.final_net_pay != null) return s.final_net_pay;

  const gross = s.gross_revenue || 0;
  // `s.carrier_take_amount || gross*0.15` couldn't tell $0 (from a
  // deliberate 0% Carrier Take) from "not recorded".
  const carrier = numOrDefault(s.carrier_take_amount, gross * 0.15);
  const dispatch = s.dispatch_fee_amount || 0;
  const fuel = s.fuel_advance_total || 0;
  // markLoadDelivered (ActiveLoad.jsx) deducts these from net_driver_pay
  // at settlement creation, but this recompute (used whenever
  // final_net_pay hasn't been set yet) was dropping them entirely --
  // a driver's shown "net pay" overstated what they'd actually be paid
  // on any settlement with a lumper or late fee.
  const lumper = s.lumper_fee_deducted || 0;
  const late = s.late_fee_deducted || 0;
  const base = gross - carrier - dispatch - fuel - lumper - late;

  const items = lineItemsMap[s.id] || [];
  const manualAdj = items.reduce((sum, item) => {
    const amt = Math.abs(item.amount || 0);
    return sum + (item.type === 'earning' ? amt : -amt);
  }, 0);

  return base + manualAdj;
}

// Native-app-only replacement for the "4 stat cards + two side-by-side
// breakdown cards" desktop layout below. That layout is ~4 screens of
// scrolling on a phone before a driver sees the one number they actually
// opened the page for. This collapses it to: hero take-home number, a
// dense list for the 4 lifetime stats, and the weekly loads/deductions
// breakdown tucked behind a single collapsed accordion. Web/desktop is
// untouched -- this only renders when isNativePlatform() is true.
function MobileFinancialSummary({
  weekLabel, thisWeekNet, thisWeekEarnings, weeklyFixedTotal,
  totalGross, totalPreLoadDeductions, trueNetTakeHome, weeksElapsed,
  thisWeekSettlements, thisWeekGross, thisWeekCarrierFees, thisWeekDispatchFees,
  thisWeekFuelAdvances, allDeductionsTotal,
  weeklyInsurance, weeklyEld, weeklyTruckPayment, weeklyTrailerPayment,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isPositive = thisWeekNet >= 0;
  const fmt = (n) => `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const stats = [
    { icon: DollarSign, label: 'Total Gross Revenue', value: `$${totalGross.toLocaleString()}`, color: 'text-primary', bg: 'bg-primary/10' },
    { icon: Minus, label: 'Pre-Load Deductions', value: `-$${totalPreLoadDeductions.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, color: 'text-red-400', bg: 'bg-red-500/10' },
    { icon: Shield, label: 'Weekly Fixed Costs', value: `-$${weeklyFixedTotal.toFixed(2)}/wk`, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { icon: TrendingUp, label: `Net Take-Home (${weeksElapsed}wk)`, value: `${trueNetTakeHome < 0 ? '-' : ''}$${Math.abs(trueNetTakeHome).toLocaleString('en-US', { maximumFractionDigits: 0 })}`, color: trueNetTakeHome >= 0 ? 'text-green-400' : 'text-red-400', bg: trueNetTakeHome >= 0 ? 'bg-green-500/10' : 'bg-red-500/10' },
  ];

  const deductionRows = [
    { label: 'Carrier Fee', value: thisWeekCarrierFees },
    { label: 'Dispatch Fee', value: thisWeekDispatchFees },
    { label: 'Fuel Advance', value: thisWeekFuelAdvances },
    { label: 'Insurance', value: weeklyInsurance, tint: true },
    { label: 'ELD', value: weeklyEld, tint: true },
    { label: 'Truck Payment', value: weeklyTruckPayment, tint: true },
    { label: 'Trailer Payment', value: weeklyTrailerPayment, tint: true },
  ].filter(r => r.value > 0);

  return (
    <div className="space-y-3 w-full min-w-0">
      {/* Hero — the one number a driver opens this page to check */}
      <div
        className={cn("glass-card p-4", isPositive ? "border border-green-500/30" : "border border-red-500/30")}
        style={{ boxShadow: isPositive ? '0 0 20px rgba(74,222,128,0.07)' : '0 0 20px rgba(239,68,68,0.07)' }}
      >
        <p className="text-xs text-muted-foreground">{weekLabel}</p>
        <p className="text-sm font-semibold text-foreground mt-0.5">Est. Take-Home This Week</p>
        <p className={cn("text-3xl font-space font-bold mt-1", isPositive ? "text-green-400" : "text-red-400")}>
          {thisWeekNet < 0 ? '−' : ''}{fmt(thisWeekNet)}
        </p>
        <p className="text-xs text-muted-foreground mt-1 font-mono">
          {fmt(thisWeekEarnings)} loads − {fmt(weeklyFixedTotal)} fixed
        </p>
      </div>

      {/* Compact lifetime stats — one dense list instead of 4 full-height cards */}
      <div className="glass-card divide-y divide-border overflow-hidden">
        {stats.map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} className="flex items-center gap-3 px-4 py-3">
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", bg)}>
              <Icon className={cn("w-4 h-4", color)} />
            </div>
            <span className="text-sm text-muted-foreground flex-1 min-w-0 truncate">{label}</span>
            <span className={cn("text-sm font-bold flex-shrink-0", color)}>{value}</span>
          </div>
        ))}
      </div>

      {/* This week's loads + deductions — collapsed by default */}
      <div className="glass-card overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          onClick={() => setDetailsOpen(o => !o)}
        >
          <span className="text-sm font-semibold text-foreground">This Week's Breakdown</span>
          {detailsOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        {detailsOpen && (
          <div className="px-4 pb-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-2">Loads</p>
              {thisWeekSettlements.length > 0 ? (
                <div className="space-y-1.5">
                  {thisWeekSettlements.map(s => (
                    <div key={s.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5">
                      <span className="text-sm text-foreground">Load #{s.load_number}</span>
                      <span className="text-sm font-semibold text-foreground">${(s.gross_revenue || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <span className="text-xs font-bold text-foreground">Total Gross</span>
                    <span className="text-xs font-bold text-emerald-400">${thisWeekGross.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground py-2">No loads settled yet this week.</p>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-2">Deductions</p>
              {deductionRows.length > 0 ? (
                <div className="space-y-1.5">
                  {deductionRows.map(({ label, value, tint }) => (
                    <div key={label} className={cn("flex items-center justify-between px-3 py-2 rounded-lg", tint ? "bg-blue-500/5" : "bg-white/5")}>
                      <span className="text-sm text-foreground">{label}</span>
                      <span className="text-sm font-semibold text-red-400">−${value.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                    <span className="text-xs font-bold text-foreground">Total Deductions</span>
                    <span className="text-xs font-bold text-red-400">−${allDeductionsTotal.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground py-2">No deductions this week.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Financials() {
  const { user } = useAuth();
  const [settlements, setSettlements] = useState([]);
  const [lineItemsMap, setLineItemsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [activeTab, setActiveTab] = useState('settlements');
  const [weeklyInsurance, setWeeklyInsurance] = useState(0);
  const [weeklyEld, setWeeklyEld] = useState(0);
  const [weeklyTruckPayment, setWeeklyTruckPayment] = useState(0);
  const [weeklyTrailerPayment, setWeeklyTrailerPayment] = useState(0);
  const [hireDate, setHireDate] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);
  const [showTaxForm, setShowTaxForm] = useState(false);

  useEffect(() => {
    if (user?.email) loadData();
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) return;
    const unsubSett = client.entities.Settlement.subscribe(() => { loadData(); });
    const unsubItems = client.entities.SettlementLineItem.subscribe(() => { loadData(); });
    return () => { unsubSett(); unsubItems(); };
  }, [user?.email]);

  const loadData = async () => {
    if (!user?.email) return;
    setLoading(true);

    const [settlementsData, allSettings, lineItemsData] = await Promise.all([
      client.entities.Settlement.filter({ driver_email: user.email }, '-created_date', 50),
      client.entities.CompanySettings.list(),
      client.entities.SettlementLineItem.filter({ driver_email: user.email }, '-created_date', 500),
    ]);

    setSettlements(settlementsData);

    const map = {};
    for (const item of lineItemsData) {
      if (!map[item.settlement_id]) map[item.settlement_id] = [];
      map[item.settlement_id].push(item);
    }
    setLineItemsMap(map);

    // Company-wide weekly fixed deductions (insurance + ELD)
    const insuranceSetting = allSettings.find(s => s.key === 'insurance_deduction');
    const eldSetting = allSettings.find(s => s.key === 'eld_weekly_cost');

    const profile = await findMyDriverProfile(user.email);
    setDriverProfile(profile);
    const isOwnerOperator = profile?.driver_type === 'Owner Operator';

    // Insurance/ELD are the DRIVER'S OWN truck insurance + ELD subscription --
    // real costs only an Owner Operator (who owns/leases their own truck)
    // actually carries. A Company Driver runs carrier-owned equipment with
    // carrier-paid insurance/ELD, so these never applied to them; they were
    // being deducted here purely because these two settings are global
    // CompanySettings values with no driver_type gate at all.
    setWeeklyInsurance(isOwnerOperator ? parseFloat(insuranceSetting?.value) || 0 : 0);
    setWeeklyEld(isOwnerOperator ? parseFloat(eldSetting?.value) || 0 : 0);
    setWeeklyTruckPayment(profile?.weekly_truck_payment ? Number(profile.weekly_truck_payment) : 0);
    setWeeklyTrailerPayment(profile?.weekly_trailer_payment ? Number(profile.weekly_trailer_payment) : 0);
    setHireDate(profile?.hire_date || null);

    setLoading(false);
  };

  // Top summary reflects everything the driver has earned to date --
  // paid or still pending payroll -- not just finalized settlements.
  // A driver with real, unpaid settlements was seeing an all-$0 summary
  // bar before their first payroll run, which reads as the dashboard
  // being broken rather than "nothing paid out yet" (the Settlement
  // History list below already shows each one's actual Paid/Pending
  // status individually).
  const isOwnerOperator = driverProfile?.driver_type === 'Owner Operator';
  const totalGross = settlements.reduce((s, x) => s + (x.gross_revenue || 0), 0);
  // carrier_take_amount means "what the carrier kept" for a Company Driver
  // on a configured pay rate (see ActiveLoad.jsx's markLoadDelivered) --
  // never money actually deducted from THEIR pay, which is computed
  // straight from their rate minus advances/lumper/late only. Counting it
  // here as a driver-facing "deduction" was showing a Company Driver a
  // number that had nothing to do with what they were ever promised.
  const totalCarrierFees = isOwnerOperator
    ? settlements.reduce((s, x) => s + (x.carrier_take_amount || (x.gross_revenue || 0) * 0.15), 0)
    : 0;
  const totalFuelAdvances = settlements.reduce((s, x) => s + (x.fuel_advance_total || 0), 0);
  const totalPreLoadDeductions = totalCarrierFees + totalFuelAdvances;
  const totalNet = settlements.reduce((s, x) => s + computeNetPay(x, lineItemsMap), 0);

  const weeklyFixedTotal = weeklyInsurance + weeklyEld + weeklyTruckPayment + weeklyTrailerPayment;

  const now = new Date();
  // Weeks of fixed costs owed so far -- must be anchored to when the
  // driver actually started (or, failing that, their earliest
  // settlement), never the calendar year. Anchoring to Jan 1 of the
  // current year multiplied fixed costs by however many weeks had
  // elapsed in the YEAR regardless of driver tenure -- a driver hired
  // last month showed ~30 weeks of fixed costs after the year progressed,
  // producing a wildly negative "Est. Net Take-Home" with no relationship
  // to what they'd actually been charged.
  const earliestSettlementDate = settlements.reduce((min, s) => {
    const d = s.finalized_date || s.settlement_date || s.created_date;
    return d && (!min || d < min) ? d : min;
  }, null);
  const fixedCostsSinceDate = hireDate || earliestSettlementDate;
  const weeksElapsed = fixedCostsSinceDate
    ? Math.max(1, Math.ceil((now - new Date(fixedCostsSinceDate)) / (7 * 24 * 60 * 60 * 1000)))
    : 1;
  const totalWeeklyFixedCosts = weeklyFixedTotal * weeksElapsed;
  const trueNetTakeHome = totalNet - totalWeeklyFixedCosts;

  const getWeekStart = () => {
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(now);
    mon.setDate(now.getDate() + diff);
    mon.setHours(0, 0, 0, 0);
    return mon;
  };
  const weekStart = getWeekStart();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const weekLabel = `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  const thisWeekSettlements = settlements.filter(s => {
    const dateStr = s.finalized_date || s.settlement_date || s.created_date;
    if (!dateStr) return false;
    return dateStr.slice(0, 10) >= weekStart.toISOString().slice(0, 10);
  });
  const thisWeekEarnings = thisWeekSettlements.reduce((sum, s) => sum + computeNetPay(s, lineItemsMap), 0);
  const thisWeekNet = thisWeekEarnings - weeklyFixedTotal;

  // Company Driver pay isn't revenue-share -- it's a flat rate (per-mile or
  // % of the load) set in DriverFormModal. Sum this week's settlements'
  // miles for the miles-based summary card below.
  const thisWeekMiles = thisWeekSettlements.reduce((sum, s) => sum + (s.miles || 0), 0);

  // Feeds MobileFinancialSummary only -- computed additively so the
  // desktop/web JSX below (which computes its own copies inside its
  // IIFEs) is untouched.
  const mobThisWeekGross = thisWeekSettlements.reduce((s, x) => s + (x.gross_revenue || 0), 0);
  const mobThisWeekCarrierFees = thisWeekSettlements.reduce((s, x) => s + (x.carrier_take_amount || (x.gross_revenue || 0) * 0.15), 0);
  const mobThisWeekDispatchFees = thisWeekSettlements.reduce((s, x) => s + (x.dispatch_fee_amount || 0), 0);
  const mobThisWeekFuelAdvances = thisWeekSettlements.reduce((s, x) => s + (x.fuel_advance_total || 0), 0);
  const mobAllDeductionsTotal = mobThisWeekCarrierFees + mobThisWeekDispatchFees + mobThisWeekFuelAdvances + weeklyFixedTotal;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Same gate as Load Board / Active Load -- a driver who isn't approved
  // yet has no real settlements to show, and this page was previously
  // rendering a full (empty) Financial Dashboard regardless.
  if (driverProfile?.approval_status !== 'Approved') {
    return <PendingApprovalNotice status={driverProfile?.approval_status} />;
  }

  return (
    <div className="flex flex-col w-full min-w-0 overflow-x-hidden space-y-4">
      <div>
        <h1 className="font-space text-2xl font-bold text-foreground truncate">Financial Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1 whitespace-normal break-words">Full earnings transparency</p>
      </div>

      {/* Missing W-4 (Company Driver) / W-9 (Owner Operator) -- same gate
          as the one on My Profile (DriverProfile.jsx), surfaced here too
          since this tax-withholding form is squarely financial info and
          drivers look for it here first. Shown on both native and web --
          tax paperwork status doesn't depend on which shell they're in. */}
      {(isOwnerOperator ? !driverProfile?.w9_signature : !driverProfile?.w4_signature) && (
        <div className="glass-card p-5 border-amber-500/30 bg-amber-500/5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Complete your {isOwnerOperator ? 'W-9' : 'W-4'}</p>
              <p className="text-xs text-muted-foreground">Required for tax withholding — {isOwnerOperator ? '1099 reporting' : 'W-2 wages'}.</p>
            </div>
          </div>
          <Button onClick={() => setShowTaxForm(true)} size="sm" className="bg-amber-500 text-black hover:bg-amber-400">
            Complete Now
          </Button>
        </div>
      )}

      {isNativePlatform() ? (
        <MobileFinancialSummary
          weekLabel={weekLabel}
          thisWeekNet={thisWeekNet}
          thisWeekEarnings={thisWeekEarnings}
          weeklyFixedTotal={weeklyFixedTotal}
          totalGross={totalGross}
          totalPreLoadDeductions={totalPreLoadDeductions}
          trueNetTakeHome={trueNetTakeHome}
          weeksElapsed={weeksElapsed}
          thisWeekSettlements={thisWeekSettlements}
          thisWeekGross={mobThisWeekGross}
          thisWeekCarrierFees={mobThisWeekCarrierFees}
          thisWeekDispatchFees={mobThisWeekDispatchFees}
          thisWeekFuelAdvances={mobThisWeekFuelAdvances}
          allDeductionsTotal={mobAllDeductionsTotal}
          weeklyInsurance={weeklyInsurance}
          weeklyEld={weeklyEld}
          weeklyTruckPayment={weeklyTruckPayment}
          weeklyTrailerPayment={weeklyTrailerPayment}
        />
      ) : (
      <>
      {/* Stats — 4 cards. Owner Operator layout is unchanged; Company
          Driver swaps the (always-$0 for them) Weekly Fixed Costs card
          for their actual pay-rate model, since their comp isn't a
          revenue split -- it's a flat per-mile or per-load percentage
          rate set by their carrier. Total Gross Revenue is a load-level
          figure (what the LOAD billed), not what the driver was ever
          promised -- Owner Operator only, same reasoning as Total
          Pre-Load Deductions above. */}
      <div className={cn(
        'flex flex-col sm:grid gap-4 w-full max-w-full m-0 min-w-0',
        // Company Driver only ever has 2 cards (no Total Gross Revenue or
        // Total Pre-Load Deductions) -- a fixed column count left a
        // permanent empty slot.
        isOwnerOperator ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2'
      )}>
        {isOwnerOperator && (
          <StatCard icon={DollarSign} label="Total Gross Revenue" value={`$${totalGross.toLocaleString()}`} sub="All settlements — paid + pending" color="text-primary" bg="bg-primary/10" />
        )}
        {isOwnerOperator && (
          <StatCard icon={Minus} label="Total Pre-Load Deductions" value={`-$${totalPreLoadDeductions.toLocaleString('en-US', { maximumFractionDigits: 0 })}`} sub="Carrier fees + fuel advances only" color="text-red-400" bg="bg-red-500/10" />
        )}
        {isOwnerOperator ? (
          <StatCard icon={Shield} label="Weekly Fixed Costs" value={`-$${weeklyFixedTotal.toFixed(2)}/wk`} sub="Deducted weekly — not per load" color="text-blue-400" bg="bg-blue-500/10" />
        ) : (
          <StatCard
            icon={Route}
            label="This Week's Miles"
            value={`${thisWeekMiles.toLocaleString()} mi`}
            sub={
              driverProfile?.pay_type === 'percentage'
                ? `Paid ${driverProfile?.pay_percentage || 0}% of load gross`
                : driverProfile?.pay_type === 'mileage'
                  ? `Paid $${(driverProfile?.pay_per_mile || 0).toFixed(2)}/mi`
                  : 'Pay rate not yet set — contact your dispatcher'
            }
            color="text-blue-400"
            bg="bg-blue-500/10"
          />
        )}
        <StatCard icon={TrendingUp} label="Est. Net Take-Home" value={`${trueNetTakeHome < 0 ? '-' : ''}$${Math.abs(trueNetTakeHome).toLocaleString('en-US', { maximumFractionDigits: 0 })}`} sub={isOwnerOperator ? `Paid + pending, after ${weeksElapsed} wk of fixed costs` : 'Paid + pending settlements'} color={trueNetTakeHome >= 0 ? "text-green-400" : "text-red-400"} bg={trueNetTakeHome >= 0 ? "bg-green-500/10" : "bg-red-500/10"} />
      </div>

      {/* Two-Section Breakdown */}
      {(() => {
        const thisWeekGross = thisWeekSettlements.reduce((s, x) => s + (x.gross_revenue || 0), 0);
        // `x.carrier_take_amount || gross*0.15` couldn't tell "$0
        // carrier fee because Carrier Take is set to 0%" from "not
        // recorded" -- silently forced 15% of gross onto a $0 row.
        // Owner Operator only -- see totalCarrierFees above for why this
        // isn't a real deduction from a Company Driver's own pay.
        const thisWeekCarrierFees = isOwnerOperator
          ? thisWeekSettlements.reduce((s, x) => s + numOrDefault(x.carrier_take_amount, (x.gross_revenue || 0) * 0.15), 0)
          : 0;
        const thisWeekDispatchFees = thisWeekSettlements.reduce((s, x) => s + (x.dispatch_fee_amount || 0), 0);
        const thisWeekFuelAdvances = thisWeekSettlements.reduce((s, x) => s + (x.fuel_advance_total || 0), 0);
        const totalPerLoadDeductions = thisWeekCarrierFees + thisWeekDispatchFees + thisWeekFuelAdvances;
        const allDeductionsTotal = totalPerLoadDeductions + weeklyFixedTotal;

        return (
          <div className={cn('grid grid-cols-1 gap-4 w-full min-w-0', isOwnerOperator && 'md:grid-cols-2')}>
            {/* Section 1 — This Week's Loads (Gross only) */}
            <div className="glass-card p-4">
              <p className="text-xs text-muted-foreground mb-1 pl-1">{weekLabel}</p>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                <h3 className="font-space font-semibold text-foreground text-sm">Section 1 — This Week's Loads</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3 pl-4">Loads settled Mon–Mon this pay period</p>
              <div className="space-y-2">
                {thisWeekSettlements.length > 0 ? (
                  <>
                    {thisWeekSettlements.map(s => (
                      <div key={s.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-white/5">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                          <span className="text-sm text-foreground">Load #{s.load_number}</span>
                          {s.origin && s.destination && (
                            <span className="text-xs text-muted-foreground hidden sm:inline">{s.origin} → {s.destination}</span>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-foreground">${(s.gross_revenue || 0).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mt-1">
                      <span className="text-sm font-bold text-foreground">Total Gross This Week</span>
                      <span className="text-sm font-bold text-emerald-400">${thisWeekGross.toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-4 px-2 leading-relaxed">No loads settled yet this week — check back after your next delivery is finalized.</p>
                )}
              </div>
            </div>

            {/* Section 2 — All Deductions This Week -- Owner Operator only.
                Dispatch Fee and weekly fixed costs are already always $0 for
                a Company Driver, and Carrier Fee never applied to their own
                pay (see totalCarrierFees above) -- the only thing that could
                ever land here for them is a fuel advance, which already
                shows per-load in Section 1's Settlement History. Nothing
                left worth a whole card. */}
            {isOwnerOperator && (
              <div className="glass-card p-4 border border-red-500/15">
                <p className="text-xs text-muted-foreground mb-1 pl-1">{weekLabel}</p>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                  <h3 className="font-space font-semibold text-foreground text-sm">Section 2 — All Deductions</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3 pl-4">Per-load + weekly fixed costs</p>
                <div className="space-y-2">
                  {/* Per-load deductions */}
                  {thisWeekSettlements.length > 0 && (
                    <>
                      <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-white/5">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-rose-400 flex-shrink-0" />
                          {/* No fixed % -- aggregates possibly-multiple settlements from
                              this week, each already correctly percent-derived on its own. */}
                          <span className="text-sm text-foreground">Carrier Fee</span>
                          <span className="text-xs text-muted-foreground">{thisWeekSettlements.length} load{thisWeekSettlements.length !== 1 ? 's' : ''}</span>
                        </div>
                        <span className="text-sm font-semibold text-red-400">−${thisWeekCarrierFees.toFixed(2)}</span>
                      </div>
                      {thisWeekDispatchFees > 0 && (
                        <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-white/5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
                            <span className="text-sm text-foreground">Dispatch Fee</span>
                          </div>
                          <span className="text-sm font-semibold text-red-400">−${thisWeekDispatchFees.toFixed(2)}</span>
                        </div>
                      )}
                      {thisWeekFuelAdvances > 0 && (
                        <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-white/5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                            <span className="text-sm text-foreground">Fuel Advance Repayment</span>
                          </div>
                          <span className="text-sm font-semibold text-red-400">−${thisWeekFuelAdvances.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  {/* Weekly fixed deductions */}
                  {weeklyInsurance > 0 && (
                    <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-blue-500/5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                        <span className="text-sm text-foreground">Insurance — weekly</span>
                      </div>
                      <span className="text-sm font-semibold text-red-400">−${weeklyInsurance.toFixed(2)}</span>
                    </div>
                  )}
                  {weeklyEld > 0 && (
                    <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-blue-500/5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
                        <span className="text-sm text-foreground">ELD — weekly</span>
                      </div>
                      <span className="text-sm font-semibold text-red-400">−${weeklyEld.toFixed(2)}</span>
                    </div>
                  )}
                  {weeklyTruckPayment > 0 && (
                    <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-blue-500/5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0" />
                        <span className="text-sm text-foreground">Truck Payment — weekly</span>
                      </div>
                      <span className="text-sm font-semibold text-red-400">−${weeklyTruckPayment.toFixed(2)}</span>
                    </div>
                  )}
                  {weeklyTrailerPayment > 0 && (
                    <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-blue-500/5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-300 flex-shrink-0" />
                        <span className="text-sm text-foreground">Trailer Payment — weekly</span>
                      </div>
                      <span className="text-sm font-semibold text-red-400">−${weeklyTrailerPayment.toFixed(2)}</span>
                    </div>
                  )}
                  {allDeductionsTotal === 0 && thisWeekSettlements.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">No deductions this week.</p>
                  )}
                  {(allDeductionsTotal > 0 || thisWeekSettlements.length > 0) && (
                    <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 mt-1">
                      <span className="text-sm font-bold text-foreground">Total Deductions This Week</span>
                      <span className="text-sm font-bold text-red-400">−${allDeductionsTotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Combined Take-Home Row -- Owner Operator only. Weekly fixed costs
          (insurance/ELD/truck/trailer payments) don't apply to a Company
          Driver at all, so this whole "loads minus fixed costs" breakdown
          card was pure dead weight for them -- their weekly figure is
          already covered by the "This Week's Miles" stat card above. */}
      {isOwnerOperator && (() => {
        const takeHome = thisWeekEarnings - weeklyFixedTotal;
        const isPositive = takeHome >= 0;
        const fmt = (n) => `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const formula = `${fmt(thisWeekEarnings)} loads − ${fmt(weeklyFixedTotal)} fixed = ${takeHome < 0 ? '−' : ''}${fmt(takeHome)}`;
        return (
          <div className={cn(
            "glass-card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3",
            isPositive ? "border border-green-500/30" : "border border-red-500/30"
          )}
            style={{ boxShadow: isPositive ? '0 0 20px rgba(74,222,128,0.07)' : '0 0 20px rgba(239,68,68,0.07)' }}
          >
            <div>
              <p className="text-sm font-semibold text-foreground">Est. Take-Home This Week</p>
              <p className="text-xs text-muted-foreground mt-0.5">After load deductions + weekly fixed costs</p>
            </div>
            <div className="text-right sm:text-right">
              <p className={cn("text-2xl font-space font-bold", isPositive ? "text-green-400" : "text-red-400")}>
                {takeHome < 0 ? '−' : ''}{fmt(takeHome)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formula}</p>
            </div>
          </div>
        );
      })()}
      </>
      )}

      {/* Tabs -- Expenses (personal operating costs) and IFTA (per-truck
          fuel tax mileage) are both Owner Operator concerns. A Company
          Driver runs carrier-owned equipment with no operating costs of
          their own to expense, and IFTA reporting is the carrier's
          responsibility for its own fleet, not something a driver tracks
          per-driver -- so with only Settlements left, there's nothing to
          switch between. */}
      {isOwnerOperator && (
        <div className="flex gap-1 p-1 bg-white/5 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('settlements')}
            className={cn("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all", activeTab === 'settlements' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
          >
            <FileText className="w-4 h-4" /> Settlements
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={cn("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all", activeTab === 'expenses' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
          >
            <Receipt className="w-4 h-4" /> Expenses
          </button>
          <button
            onClick={() => setActiveTab('ifta')}
            className={cn("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all", activeTab === 'ifta' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
          >
            <Map className="w-4 h-4" /> IFTA
          </button>
        </div>
      )}

      {isOwnerOperator && activeTab === 'expenses' && <ExpensesTab ytdNetEarnings={totalNet} />}
      {isOwnerOperator && activeTab === 'ifta' && <IFTATab />}

      {/* Settlement History */}
      {activeTab === 'settlements' && <div className="glass-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary" />
          <h3 className="font-space font-semibold text-foreground">Settlement History</h3>
          <span className="ml-auto text-xs text-muted-foreground">{settlements.length} settlement{settlements.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin mx-auto" />
          </div>
        ) : settlements.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No settlements yet. Complete your first load to see earnings here.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {settlements.map(s => {
              const hasWeeklyFixed = weeklyInsurance > 0 || weeklyEld > 0 || weeklyTruckPayment > 0 || weeklyTrailerPayment > 0;

              return (
                <div key={s.id}>
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/3 transition-colors text-left"
                    onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", (s.is_finalized || s.status === 'Paid') ? 'bg-green-500/10' : 'bg-white/5')}>
                        <FileText className={cn("w-4 h-4", (s.is_finalized || s.status === 'Paid') ? 'text-green-400' : 'text-muted-foreground')} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Load #{s.load_number}</p>
                        <p className="text-xs text-muted-foreground">{s.is_finalized ? `Finalized ${formatFinalizedDate(s.finalized_date)}` : s.settlement_date}</p>
                        {s.payment_reference && (
                          <p className="text-xs text-primary/80 font-mono mt-0.5">Ref: {s.payment_reference}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm font-bold text-green-400">${computeNetPay(s, lineItemsMap).toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{s.is_finalized ? 'final pay' : 'est. net pay'}</p>
                      </div>
                      <span className={cn("text-xs font-semibold px-2.5 py-0.5 rounded-full border hidden sm:inline-flex", statusColors[s.status])}>
                        {s.status}
                      </span>
                      {s.pdf_url && (
                        <a
                          href={s.pdf_url}
                          download={`settlement-${s.load_number}-${s.finalized_date || s.settlement_date}.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 transition-colors"
                        >
                          <Download className="w-3 h-3" /> PDF
                        </a>
                      )}
                      {s.status === 'Paid' && !s.pdf_url && (
                        <button
                          onClick={e => { e.stopPropagation(); toast.info('Contact admin to regenerate your statement.'); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg text-xs font-semibold hover:bg-amber-500/20 transition-colors"
                        >
                          <AlertCircle className="w-3 h-3" /> No PDF
                        </button>
                      )}
                      {expanded === s.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </button>

                  {expanded === s.id && (
                    <div className="px-3 md:px-5 pb-5 bg-black/20 space-y-3 pt-4">
                      {/* Section 1: Per-load */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Section 1 — This Load's Deductions</p>
                        <div className="glass-card-dark rounded-xl divide-y divide-border overflow-hidden">
                          <div className="flex justify-between px-4 py-2.5 text-sm">
                            <span className="text-muted-foreground">Gross Revenue</span>
                            <span className="font-semibold text-foreground">${(s.gross_revenue || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between px-4 py-2.5 text-sm">
                            <span className="text-muted-foreground">
                              {s.pay_model === 'mileage'
                                ? `Retained by carrier (you're paid $${(s.miles ? (s.gross_revenue - s.carrier_take_amount) / s.miles : 0).toFixed(2)}/mi × ${s.miles || 0} mi)`
                                : s.pay_model === 'percentage'
                                  ? `Retained by carrier (you're paid ${(100 - numOrDefault(s.carrier_take_percent, 0)).toFixed(0)}% of gross)`
                                  : `Carrier Fee (${numOrDefault(s.carrier_take_percent, 15)}%)`}
                            </span>
                            <span className="font-semibold text-red-400">−${numOrDefault(s.carrier_take_amount, (s.gross_revenue || 0) * 0.15).toFixed(2)}</span>
                          </div>
                          {(s.dispatch_fee_amount || 0) > 0 && (
                            <div className="flex justify-between px-4 py-2.5 text-sm">
                              <span className="text-muted-foreground">Dispatch Fee ({numOrDefault(s.dispatch_fee_percent, 3)}%)</span>
                              <span className="font-semibold text-red-400">−${(s.dispatch_fee_amount || 0).toFixed(2)}</span>
                            </div>
                          )}
                          {(s.fuel_advance_requested || s.fuel_advance_total || 0) > 0 && (
                            <div className="flex justify-between px-4 py-2.5 text-sm">
                              <span className="text-muted-foreground">Fuel Advance Repayment</span>
                              <span className="font-semibold text-amber-400">−${(s.fuel_advance_requested || s.fuel_advance_total || 0).toFixed(2)}</span>
                            </div>
                          )}
                          {(s.fuel_advance_documented || 0) > 0 && (
                            <div className="flex justify-between px-4 py-2.5 text-sm">
                              <span className="text-muted-foreground">Receipts Documented</span>
                              <span className="font-semibold text-green-400">+${(s.fuel_advance_documented || 0).toFixed(2)}</span>
                            </div>
                          )}
                          {(lineItemsMap[s.id] || []).map((item, i) => (
                            <div key={i} className="flex justify-between px-4 py-2 text-sm">
                              <span className="text-muted-foreground">{item.category} — {item.memo}</span>
                              <span className={item.type === 'earning' ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
                                {item.type === 'earning' ? '+' : '−'}${Math.abs(item.amount || 0).toFixed(2)}
                              </span>
                            </div>
                          ))}
                          <div className="flex justify-between px-4 py-3 bg-green-500/5 text-sm">
                            <span className="font-bold text-foreground">Net Driver Pay (this load)</span>
                            <span className="font-bold text-green-400 text-base">${computeNetPay(s, lineItemsMap).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Weekly fixed — info only */}
                      {hasWeeklyFixed && (
                        <div>
                          <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide mb-2">Section 2 — Weekly Fixed Deductions <span className="normal-case font-normal text-muted-foreground">(deducted weekly — not from this load)</span></p>
                          <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 divide-y divide-blue-500/10 overflow-hidden">
                            {weeklyInsurance > 0 && (
                              <div className="flex justify-between px-4 py-2.5 text-sm">
                                <span className="text-blue-200">Insurance — weekly</span>
                                <span className="text-blue-300">−${weeklyInsurance.toFixed(2)}</span>
                              </div>
                            )}
                            {weeklyEld > 0 && (
                              <div className="flex justify-between px-4 py-2.5 text-sm">
                                <span className="text-blue-200">ELD — weekly</span>
                                <span className="text-blue-300">−${weeklyEld.toFixed(2)}</span>
                              </div>
                            )}
                            {weeklyTruckPayment > 0 && (
                              <div className="flex justify-between px-4 py-2.5 text-sm">
                                <span className="text-blue-200">Truck Payment — weekly</span>
                                <span className="text-blue-300">−${weeklyTruckPayment.toFixed(2)}</span>
                              </div>
                            )}
                            {weeklyTrailerPayment > 0 && (
                              <div className="flex justify-between px-4 py-2.5 text-sm">
                                <span className="text-blue-200">Trailer Payment — weekly</span>
                                <span className="text-blue-300">−${weeklyTrailerPayment.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {showTaxForm && (
        <CompleteTaxFormModal
          driver={driverProfile}
          onClose={() => setShowTaxForm(false)}
          onSaved={loadData}
        />
      )}
    </div>
  );
}